import { useEffect, useState, type SetStateAction, useRef } from "react";
import GameTopBar from "./GameTopBar";
import Result from "./Result";

interface Math {
  operand1: number;
  operator: string;
  operand2: number;
  operator2?: string;
  operand3?: number;
}

interface IOppDetails {
  userId: string;
  username: string;
}

interface IMathMainContent {
  operand1: number;
  operator1: string;
  operand2: number;
  operator2?: string;
  operand3?: number;
  websocket: WebSocket | null;
  setQuestions: React.Dispatch<SetStateAction<Math | null>>;
  rating: number;
  score: number;
  oppScore: number;
  setScore: React.Dispatch<SetStateAction<number>>;
  setOppScore: React.Dispatch<SetStateAction<number>>;
  oppPlayerDetails: IOppDetails | null;
  oppRating: number;
  type: string;
}

function MathMainContent({
  operand1, operator1, operand2, operator2, operand3, websocket, setQuestions, rating, score, setOppScore,
  oppScore, setScore, oppPlayerDetails, oppRating, type
}: IMathMainContent) {
  const [timer, setTimer] = useState<number>(60);
  const [ans, setAns] = useState<string>("");
  const [showResult, setShowResult] = useState<boolean>(false);
  const [isIncorrect, setIsIncorrect] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [operand1]);

  useEffect(() => {
    if (timer <= 0 && !showResult) {
      // console.log("Inside the timer in fe");
      setShowResult(true);
      // console.log(websocket);
      // console.log(websocket?.readyState);
      if (websocket && websocket.readyState === WebSocket.OPEN) { // kyuki what if they leaves mid game
        // console.log("Inside the iif condition");
          websocket.send(JSON.stringify({
             type: "endgame",
             rating: rating
        }));
      }
      return;
    }
    if (timer <= 0) return;

    const time = setInterval(() => {
      setTimer((t) => t - 1);
    }, 1000);

    return () => clearInterval(time);
  }, [timer, showResult, websocket]);

  function submitAnswer(e: React.FormEvent) {
    e.preventDefault();
    if (!ans) return;

    websocket?.send(JSON.stringify({
      type: type,
      ans: ans,
      rating: rating
    }));

    websocket!.onmessage = (msg) => {
      const parse = JSON.parse(msg.data);
      if (parse.message === "Correct answer") {
        setIsIncorrect(false);
        setAns("");
        setQuestions(parse.questions);
      } else if (parse.message === "Incorrect answer") {
        setIsIncorrect(true);
        setAns("");
        // Shake focus
        if (inputRef.current) inputRef.current.focus();
      } else if (parse.message && parse.message !== "Match Found, game starting" && parse.message !== "Waiting for another player to join") {
         // handle unexpected alerts safely
      }

      if (parse.yourScore !== undefined && parse.oppScore !== undefined) {
        setScore(parse.yourScore);
        setOppScore(parse.oppScore);
      }
    };
  }

  if (showResult) {
    return (
      <div className="flex-1 overflow-hidden">
        <Result score={score} oppScore={oppScore} />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-[#0a0a12] overflow-hidden relative">
      <GameTopBar
        timer={timer}
        userRating={rating}
        oppUsername={oppPlayerDetails?.username || "OPP"}
        oppRating={oppRating}
        score={score}
        oppScore={oppScore}
      />

      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {/* The Math Question */}
        <div className="flex flex-col items-center justify-center mb-10 w-full max-w-xl border-b border-violet-500/20 pb-8 px-2 overflow-hidden">
          <div className="font-black text-white leading-none drop-shadow-[0_0_20px_rgba(139,92,246,0.2)] flex flex-nowrap items-center justify-center gap-x-2 sm:gap-x-3 w-full transition-all duration-300 text-[42px] sm:text-[54px] tracking-wider whitespace-nowrap">
            <span>{operand1}</span>
            <span className="text-violet-500">{operator1}</span>
            <span>{operand2}</span>
            {operator2 && operand3 !== undefined && (
              <>
                <span className="text-violet-500">{operator2}</span>
                <span>{operand3}</span>
              </>
            )}
          </div>
        </div>

        {/* Input Form */}
        <form onSubmit={submitAnswer} className="w-full max-w-[280px] sm:max-w-[320px] flex flex-col items-center gap-4 relative">
          <input
            ref={inputRef}
            type="number"
            value={ans}
            onChange={(e) => {
              setAns(e.target.value);
              setIsIncorrect(false);
            }}
            placeholder="TYPE ANSWER"
            className="w-full py-3.5 sm:py-4 text-center bg-[#101020]/80 border border-violet-500/30 rounded-2xl text-[24px] font-black text-white tracking-[0.2em] focus:outline-none focus:border-violet-500 focus:bg-violet-500/10 transition-colors placeholder:text-gray-600/50 placeholder:text-lg placeholder:font-bold placeholder:tracking-widest [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]"
            autoComplete="off"
            autoFocus
          />
          <button
            type="submit"
            className="w-full py-3 sm:py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-xl text-white font-bold text-[15px] sm:text-[16px] uppercase tracking-widest hover:-translate-y-0.5 transition-transform shadow-[0_6px_20px_rgba(139,92,246,0.3)] active:translate-y-0 active:shadow-none"
          >
            Submit
          </button>
          
          {/* Error Message Space */}
          <div className="h-6 w-full flex items-center justify-center absolute -bottom-8">
            {isIncorrect && (
              <span className="text-red-400 text-[11px] sm:text-xs font-bold uppercase tracking-widest animate-pulse">
                Incorrect Answer!
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default MathMainContent;